import { FC } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import { Download, FileSpreadsheet, FileText, Image } from "lucide-react"

interface ExportMenuProps {
  /**
   * A string identifying the export type (passed back to `onExport`).
   * Examples used in the codebase: "consumption-analytics", "customer-analytics", "activity-tables"
   */
  type: string
  /**
   * Label shown on the trigger button. Defaults to "Export".
   */
  label?: string
  /**
   * Callback invoked when an export menu item is clicked.
   * Receives (type, format) where format is one of: "csv" | "pdf" | "png"
   */
  onExport: (type: string, format: "csv" | "pdf" | "png") => void
  /**
   * Additional className to apply to the trigger button.
   */
  className?: string
}

/**
 * Reusable export dropdown used across the dashboard.
 * Keeps UI consistent and delegates actual export behavior to parent via `onExport`.
 */
export const ExportMenu: FC<ExportMenuProps> = ({
  type,
  label = "Export",
  onExport,
  className,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={`gap-2 ${className || ""}`}>
          <Download className="size-4" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem onClick={() => onExport(type, "csv")}>
          <FileSpreadsheet className="size-4 mr-2" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onExport(type, "pdf")}>
          <FileText className="size-4 mr-2" />
          Export as PDF
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onExport(type, "png")}>
          <Image className="size-4 mr-2" />
          Export as PNG
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default ExportMenu
